import React, { useState, useEffect, useMemo } from 'react'

import Button from 'components_new/atoms/Button'
import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'
import IconButton from 'components_new/atoms/IconButton'
import TextField from 'components_new/atoms/TextField'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'

import PageLayout from 'components_new/templates/PageLayout'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { useSources } from 'redux/hooks/Sources'
import { useSourcesActions } from 'redux/actions/DataPlatform/connections/Sources'
import { useCredentialsGroups } from 'redux/hooks/CredentialsGroups'
import { useCredentialsGroupsActions } from 'redux/actions/CredentialsGroups'
import Endpoint from 'components_new/molecules/Endpoint'
import { Divider } from '@mui/material'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import Link from 'components_new/atoms/Link'
import { useCustomer } from 'redux/hooks/Customer'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import Chip from 'components_new/atoms/Chip'

const BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000/public/credentials'
    : 'https://portal.homepal.se/public/credentials'

const Credentials = () => {
  const [addCredentialsOpen, setAddCredentialsOpen] = useState(false)
  const [title, setTitle] = useState<string>('')
  const [showCredentialsOpen, setShowCredentialsOpen] = useState<string | null>(
    null
  )
  const [toDelete, setToDelete] = useState<string | null>(null)
  const [selectedSource, setSelectedSource] = useState<Option | null>(null)
  const [labels, setLabels] = useState<string[]>([''])
  const [sourcesFetched, sources] = useSources()
  const customer = useCustomer()
  const { isHomepal } = useAccountSettings()
  const [credentialsGroups, credentialsGroupsFetched] = useCredentialsGroups()
  const { tryGetAllSources } = useSourcesActions()
  const {
    tryCreateCredentialsGroups,
    tryDeleteCredentialsGroups,
    tryGetAllCredentialsGroups
  } = useCredentialsGroupsActions()

  const closeCredentialsDialog = () => {
    setAddCredentialsOpen(false)
    setSelectedSource(null)
    setTitle('')
    setLabels([''])
  }

  const onChangeCredentialsSource = (option: Option | null) => {
    if (option?.id && sources[option.id]) {
      const newLabels = sources[option.id].included.auth_params.map(
        (param) => param.attributes.field
      )

      setLabels(newLabels)
    }
    setSelectedSource(option)
  }

  useEffect(() => {
    if (!sourcesFetched) {
      tryGetAllSources()
    }

    if (!credentialsGroupsFetched) {
      tryGetAllCredentialsGroups()
    }
  }, [])

  const openedCredentialsGroup = useMemo(() => {
    if (showCredentialsOpen) {
      return credentialsGroups[showCredentialsOpen]
    }

    return null
  }, [showCredentialsOpen])

  const sourcesOptions: Option[] = Object.values(sources).map((source) => ({
    id: source.id,
    title: source.attributes.name
  }))

  if (!isHomepal) return null

  return (
    <PageLayout
      variant="static"
      topbar={{
        breadcrumbs: [{ title: 'Admin' }, { title: 'Hemliga uppgifter' }]
      }}
    >
      <Text>
        OBS! Du är inloggad på <b>{customer?.name}</b> och skapar hemliga
        uppgifter för denna kund.
      </Text>

      <SettingsGroup title={'Hemliga uppgifter'}>
        {Object.values(credentialsGroups)
          .sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
          .map((credentials) => (
            <SettingsItem
              actions={
                <>
                  <IconButton
                    onClick={() => setShowCredentialsOpen(credentials.id)}
                    size="small"
                  >
                    <Icon fontSize="small" name={'VisibilityOutlined'} />
                  </IconButton>
                  <IconButton
                    onClick={() => setToDelete(credentials.id)}
                    size="small"
                  >
                    <Icon fontSize="small" name="Delete" />
                  </IconButton>
                </>
              }
              alignItems="center"
              key={credentials.id}
              title={
                <>
                  {credentials.title}
                  <Chip
                    label={
                      credentials.credentials[0].value
                        ? 'Inlämnad'
                        : 'Ej inlämnad'
                    }
                    size="xsmall"
                    color={
                      credentials.credentials[0].value ? 'success' : 'warning'
                    }
                    sx={{ ml: 1 }}
                  />
                </>
              }
            />
          ))}
        <SettingsItem
          color="success"
          onClick={() => setAddCredentialsOpen(true)}
          title={'Skapa ny'}
          variant="add"
        />
      </SettingsGroup>

      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={() => closeCredentialsDialog()}>
              Stäng
            </Button>
            <Button
              variant="text"
              disabled={
                labels.length === 0 ||
                labels.some((label) => label.trim() === '') ||
                title.trim() === ''
              }
              onClick={() => {
                closeCredentialsDialog()
                tryCreateCredentialsGroups({
                  title,
                  labels
                })
              }}
            >
              Spara
            </Button>
          </>
        }
        onClose={() => closeCredentialsDialog()}
        open={addCredentialsOpen}
        title={'Nya uppgifter'}
      >
        <TextField
          label={'Titel'}
          required={true}
          autoComplete="off"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          sx={{ mt: 0, mb: 2 }}
        />
        <SelectSearch
          closeOnSelect={true}
          label={'Utgå från en källa'}
          multiple={false}
          onChange={(option) => {
            onChangeCredentialsSource(option)
          }}
          options={sourcesOptions}
          selected={selectedSource}
          sort
          sx={{ mt: 0, mb: 2 }}
        />
        <Text>Hemliga uppgifter</Text>
        {labels.map((label, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', alignItems: 'center', mt: 0, mb: 1 }}
          >
            <TextField
              label={`Uppgift ${i + 1}`}
              required={true}
              autoComplete="off"
              value={label}
              onChange={(event) =>
                setLabels((prev) => {
                  const newLabels = [...prev]

                  newLabels[i] = event.target.value

                  return newLabels
                })
              }
              sx={{ mt: 0, mb: 0 }}
            />
            <IconButton
              onClick={() =>
                setLabels((prev) => prev.filter((_, j) => j !== i))
              }
              size="medium"
            >
              <Icon fontSize="small" name="Delete" />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="text"
          onClick={() => setLabels((prev) => [...prev, ''])}
        >
          Lägg till uppgift +
        </Button>
      </SimpleDialog>
      <SimpleDialog
        actions={
          <Button variant="text" onClick={() => setShowCredentialsOpen(null)}>
            Stäng
          </Button>
        }
        onClose={() => setShowCredentialsOpen(null)}
        open={!!showCredentialsOpen}
        title={
          <>
            {openedCredentialsGroup?.title}{' '}
            <Chip
              label={
                openedCredentialsGroup?.credentials[0].value
                  ? 'Inlämnad'
                  : 'Ej inlämnad'
              }
              size="small"
              color={
                openedCredentialsGroup?.credentials[0].value
                  ? 'success'
                  : 'warning'
              }
              sx={{ ml: 1 }}
            />
          </>
        }
      >
        <Text>
          Skicka nedan länk till kunden, i länken loggar de in med lösenordet
          och anger uppgifterna.
        </Text>
        <Endpoint
          href={`${BASE_URL}/${openedCredentialsGroup?.unique_string}`}
          overrideVariant={'URL'}
          size={'small'}
        />
        <Endpoint
          href={openedCredentialsGroup?.password || ''}
          overrideVariant={'Lösenord'}
          size={'small'}
        />

        <Divider sx={{ my: 2 }} />
        <Text>Hemliga uppgifter:</Text>
        {[...(openedCredentialsGroup?.credentials || [])]
          .sort((a, b) => sortAlphabeticalAsc(a.label, b.label))
          .map((credential) => (
            <Endpoint
              disabled={credential.value === null}
              key={credential.id}
              href={credential.value || 'Ej ifylld'}
              overrideVariant={credential.label}
              size={'small'}
            />
          ))}
        <Divider sx={{ my: 2 }} />

        <Text sx={{ mb: 2 }} variant={'h6'}>
          Exempel email:
        </Text>

        <Box sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
          <Text>
            Hej,
            <br />
            <br />
            För att vi ska kunna gå vidare behöver vi att du lämnar de uppgifter
            som efterfrågats. Vi använder en säker tjänst för att ta emot
            känslig information, och dina uppgifter krypteras och hanteras på
            ett tryggt sätt. Observera att uppgifterna inte kan ändras i
            efterhand, så säkerställ att informationen stämmer innan du skickar
            in den.
            <br />
            <br />
            <Link
              target={'_blank'}
              rel={'noopener'}
              href={`${BASE_URL}/${openedCredentialsGroup?.unique_string}`}
            >
              Klicka här för att säkert ange dina uppgifter.
            </Link>
            <br />
            <b>Lösenord:</b> {openedCredentialsGroup?.password}
            <br />
            <br />
            Vänliga hälsningar,
          </Text>
        </Box>
      </SimpleDialog>
      <DeleteDialog
        onClose={() => setToDelete(null)}
        open={!!toDelete}
        onDelete={(cb) => {
          const toDeleteLocal = toDelete

          setToDelete(null)

          if (toDeleteLocal) {
            tryDeleteCredentialsGroups(toDeleteLocal)
          }
          cb()
        }}
        title={toDelete ? credentialsGroups[toDelete].title : ''}
        type={'uppgifter'}
      />
    </PageLayout>
  )
}

export default Credentials
